import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../../auth/PlatformAuthContext';
import { ipApi } from '../../services/api';
import {
  PLAN_PRESETS, STAFF_ROLES, roleLimitsForPlan, modulesForPlan, slugFromName, totalRoleLimit, pricingForPlan,
} from '../../config/plans';
import { Badge } from '../../components/Ui';

const STEPS = ['Hospital', 'Login limits', 'Owner login', 'Complete'];

export default function PlatformOnboard() {
  const { user, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    phone: '',
    email: '',
    gstin: '',
    tagline: '',
    regNo: '',
    website: '',
    city: 'Anantapur',
    state: 'AP',
    plan: 'Standard',
    slug: '',
    roleLimits: roleLimitsForPlan('Standard'),
    modules: modulesForPlan('Standard'),
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
    initialBeds: 5,
  });

  const applyPlan = (plan) => {
    setForm((f) => ({
      ...f,
      plan,
      roleLimits: roleLimitsForPlan(plan),
      modules: modulesForPlan(plan),
    }));
  };

  const setLimit = (role, value) => {
    setForm((f) => ({
      ...f,
      roleLimits: { ...f.roleLimits, [role]: Math.max(0, Number(value) || 0) },
    }));
  };

  const suggestEmail = () => {
    const slug = form.slug || slugFromName(form.name);
    setForm((f) => ({ ...f, slug, ownerEmail: `owner@${slug}.hcen` }));
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await ipApi.platformOnboardHospital({
        ...form,
        slug: form.slug || slugFromName(form.name),
        staffLimit: totalRoleLimit(form.roleLimits),
        ownerPassword: form.ownerPassword || undefined,
        pricing: pricingForPlan(form.plan),
      });
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="topbar">
        <div>
          <h1 className="topbar-title">Onboard hospital</h1>
          <p className="topbar-sub">{user?.name} — register hospital + owner login</p>
        </div>
        <div className="topbar-actions">
          <Link to="/platform/dashboard" className="btn btn-secondary btn-sm">← Dashboard</Link>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { logout(); navigate('/platform/login'); }}>Logout</button>
        </div>
      </header>

      <div className="page-body">
        <div className="onboard-steps card mb">
          {STEPS.map((label, i) => (
            <div key={label} className={`onboard-step ${step === i ? 'active' : ''} ${step > i ? 'done' : ''}`}>
              <span className="onboard-step-num">{i + 1}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {error && <div className="error mb">{error}</div>}

        {step === 0 && (
          <div className="card form-grid">
            <h3>Hospital details</h3>
            <div className="field"><label>Hospital name *</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugFromName(e.target.value) })} /></div>
            <div className="field full"><label>Street address *</label><input className="input" required placeholder="Building, street, area" value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} /></div>
            <div className="field full"><label>Address line 2</label><input className="input" placeholder="Landmark, floor (optional)" value={form.addressLine2} onChange={(e) => setForm({ ...form, addressLine2: e.target.value })} /></div>
            <div className="field"><label>City</label><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="field"><label>State</label><input className="input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div className="field"><label>Pincode</label><input className="input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            <div className="field"><label>Hospital phone</label><input className="input" placeholder="+91 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="field"><label>Hospital email</label><input className="input" type="email" placeholder="info@hospital.hcen" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="field"><label>GSTIN (optional)</label><input className="input" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
            <div className="field full"><label>Tagline</label><input className="input" placeholder="Quality care · Compassionate healing" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
            <div className="field"><label>Registration no.</label><input className="input" placeholder="Clinical establishment reg." value={form.regNo} onChange={(e) => setForm({ ...form, regNo: e.target.value })} /></div>
            <div className="field"><label>Website</label><input className="input" placeholder="www.hospital.in" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
            <p className="hint full">All fields above auto-appear on every PDF letterhead — receipts, Rx, lab, discharge.</p>
            <div className="field full">
              <label>Plan</label>
              <div className="plan-cards">
                {Object.entries(PLAN_PRESETS).map(([key, p]) => (
                  <button key={key} type="button" className={`plan-card ${form.plan === key ? 'selected' : ''}`} onClick={() => applyPlan(key)}>
                    <strong>{p.label}</strong>
                    <span className="hint">{p.description}</span>
                    <span className="hint">{totalRoleLimit(p.roleLimits)} logins</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="field"><label>Initial beds</label><input className="input" type="number" min={0} value={form.initialBeds} onChange={(e) => setForm({ ...form, initialBeds: Number(e.target.value) })} /></div>
            <div className="form-actions full">
              <button type="button" className="btn btn-primary" disabled={!form.name.trim() || !form.addressLine1.trim()} onClick={() => setStep(1)}>Next: login limits</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card">
            <h3>Staff login limits</h3>
            <p className="hint mb">Set how many logins this hospital can create per role. Hospital owner manages staff within these limits.</p>
            <div className="quota-edit-grid">
              {STAFF_ROLES.map((r) => (
                <div key={r.id} className="quota-edit-row">
                  <div>
                    <strong>{r.label}</strong>
                    <p className="hint">{r.description}</p>
                  </div>
                  <input className="input quota-input" type="number" min={0} value={form.roleLimits[r.id] ?? 0} onChange={(e) => setLimit(r.id, e.target.value)} />
                </div>
              ))}
            </div>
            <p className="hint mt"><strong>Total:</strong> {totalRoleLimit(form.roleLimits)} logins</p>
            <div className="form-actions row-actions mt">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(0)}>Back</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>Next: owner login</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card form-grid">
            <h3>Hospital owner login</h3>
            <p className="hint full">This person manages all reception, doctor, pharmacy logins for their hospital.</p>
            <div className="field"><label>Owner name *</label><input className="input" required value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} /></div>
            <div className="field">
              <label>Owner email (login) *</label>
              <input className="input" required value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} placeholder="owner@hospital.hcen" />
              <button type="button" className="btn btn-sm btn-secondary mt" onClick={suggestEmail}>Suggest from hospital name</button>
            </div>
            <div className="field">
              <label>Temporary password</label>
              <input className="input" value={form.ownerPassword} onChange={(e) => setForm({ ...form, ownerPassword: e.target.value })} placeholder="Auto-generated if empty" />
            </div>
            <div className="form-actions full row-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button type="button" className="btn btn-primary" disabled={busy || !form.ownerEmail} onClick={submit}>
                {busy ? 'Creating…' : 'Complete onboarding'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="card onboard-success">
            <h3>Hospital onboarded</h3>
            <Badge tone="success">Active</Badge>
            <p><strong>{result.hospital?.name}</strong> · {result.hospital?.plan} plan</p>
            <p>Hospital ID: <code>{result.hospital?.hospitalId}</code></p>
            <div className="owner-credentials card mt">
              <h4>Share with hospital owner</h4>
              <p>Login URL: <strong>networks.hcen.in/login</strong></p>
              <p>Email: <strong>{result.owner?.email}</strong></p>
              <p>Password: <strong>{result.owner?.temporaryPassword}</strong></p>
              <p className="hint">Owner must change password on first login. They can then add doctors, reception, pharmacy, etc. within the limits you set.</p>
            </div>
            <Link to="/platform/dashboard" className="btn btn-primary mt">Back to dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}
